import DataGrid, { DataGridRef } from '@/components/dataGrid/dataGrid';
import translate from '@/components/textData/utils';
import { Company } from '@/modules/company/entities/interfaces/company';
import { ActionsByStatus } from '@/modules/copay/components/actionsByStatus/actionsByStatus';
import { UsageStatementStatus } from '@/modules/copay/components/usageStatementStatus/usageStatementStatus';
import { TRANSLATED_USAGE_STATEMENT_PROCESS_STATUS } from '@/modules/copay/entities/constants/usageStatementProcess';
import {
  UsageStatementListFilter,
  UsageStatementProcess,
} from '@/modules/copay/entities/interfaces/usageStatements';
import { CopayService } from '@/modules/copay/services/copayService';
import ICONS from '@/shared/entities/constants/icons';
import React, { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Text } from 'vkit/lib/components';

interface UsageStatementListProps {
  company?: Company;
  companyParentId?: string;
  setOpenDialogShareCopay: Dispatch<SetStateAction<boolean>>;
  setUsageStatementWithWaitingDescriptionStatus: Dispatch<
    SetStateAction<UsageStatementProcess | null>
  >;
  instanceEmitter?: MutableRefObject<DataGridRef | undefined>;
}

export const UsageStatementList: React.FC<UsageStatementListProps> = ({
  company,
  companyParentId,
  instanceEmitter,
  setOpenDialogShareCopay,
  setUsageStatementWithWaitingDescriptionStatus,
}) => {
  return company?.companyGroupId ? (
    <DataGrid
      instanceEmitter={instanceEmitter}
      source={(filters: Partial<UsageStatementListFilter>) => {
        const { createdAt, ...rest } = filters;

        if (createdAt) {
          rest['createdAt.GTE'] = `${createdAt}T00:00:00`;
          rest['createdAt.LTE'] = `${createdAt}T23:59:59`;
        }

        if (rest.sort?.includes('competenceMonth')) {
          rest.sort = Array.isArray(rest.sort)
            ? rest.sort
            : [rest.sort.replace('competenceMonth', 'competenceYear'), rest.sort];
        }

        return new CopayService().getUsageStatementProcesses({
          sort: '-createdAt',
          companyGroupId: company.companyGroupId,
          include: ['usageStatement'],
          ...rest,
        });
      }}
      filters={[
        {
          label: 'Buscar',
          key: 'q',
          width: 33.33,
        },
        {
          label: 'Status',
          type: 'select',
          data: Object.entries(TRANSLATED_USAGE_STATEMENT_PROCESS_STATUS).map(([value, text]) => ({
            value,
            text,
          })),
          key: 'status',
          width: 33.33,
        },
        {
          label: 'Data de criação',
          type: 'date',
          key: 'createdAt',
          width: 33.33,
        },
      ]}
      columns={[
        {
          sortable: true,
          headerName: 'Mês de coparticipação',
          field: 'competenceMonth',
          render: ({ competenceMonth, competenceYear }) =>
            `${translate.month(String(competenceMonth))}/${competenceYear}`,
        },
        {
          sortable: true,
          headerName: 'Data da criação',
          formatData: 'dateWithoutTZ',
          field: 'createdAt',
        },
        {
          headerName: 'Status',
          render: ({ status, processing }) => (
            <UsageStatementStatus status={status} processing={processing} />
          ),
        },
        {
          headerName: 'Nome do arquivo',
          field: 'fileName',
        },
        {
          render: (usegeStatementProcess, refreshData) => (
            <ActionsByStatus
              usageStatementProcess={usegeStatementProcess}
              refreshData={refreshData}
              processing={usegeStatementProcess.processing}
              company={company}
              companyParentId={companyParentId}
              setOpenDialogShareCopay={setOpenDialogShareCopay}
              setUsageStatementWithWaitingDescriptionStatus={
                setUsageStatementWithWaitingDescriptionStatus
              }
            />
          ),
        },
      ]}
    />
  ) : (
    <Text
      rounded
      size='medium'
      applyBaseColor
      color='warning'
      iconColor='warning'
      padding='8px 12px'
      icon={ICONS.WARNING}
      value='Esta empresa não possui as configurações necessárias para visualização da lista de status das coparticipações.'
    />
  );
};
